import { Injectable, inject } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { environment } from "../../../../environments/environment";
import { map, switchMap } from "rxjs";
import type {
  RespuestaPaginada,
  QuoteApi,
  CreateQuoteDto,
  AddQuoteItemDto,
  UpdateQuoteItemDto,
  UpdateQuoteNotesDto,
  QuotePriceInputDto,
  QuoteStatusDto,
} from "../models/cotizaciones.model";

@Injectable({ providedIn: "root" })
export class CotizacionesService {
  private http = inject(HttpClient);

  private baseUrl = environment.baseUrl; // http://localhost:3000
  private endpoint = "quotes";

  // GET /quotes?page&limit
  list(page = 1, limit = 10): Observable<RespuestaPaginada<QuoteApi>> {
    const params = new HttpParams()
      .set("page", String(page))
      .set("limit", String(limit));

    return this.http.get<RespuestaPaginada<QuoteApi>>(
      `${this.baseUrl}/${this.endpoint}`,
      { params },
    );
  }

  // GET /quotes/:id
  getById(id: number): Observable<QuoteApi> {
    return this.http.get<QuoteApi>(`${this.baseUrl}/${this.endpoint}/${id}`);
  }

  // POST /quotes  (crear carrito DRAFT)
  create(dto: CreateQuoteDto): Observable<QuoteApi> {
    return this.http.post<QuoteApi>(`${this.baseUrl}/${this.endpoint}`, dto);
  }

  // PATCH /quotes/:id/notes
  updateNotes(id: number, dto: UpdateQuoteNotesDto): Observable<QuoteApi> {
    return this.http.patch<QuoteApi>(
      `${this.baseUrl}/${this.endpoint}/${id}/notes`,
      dto,
    );
  }

  // POST /quotes/:id/items (solo DRAFT)
  addItem(id: number, dto: AddQuoteItemDto): Observable<QuoteApi> {
    return this.http.post<QuoteApi>(
      `${this.baseUrl}/${this.endpoint}/${id}/items`,
      dto,
    );
  }

  // PATCH /quotes/:id/items/:itemId (solo DRAFT)
  updateItemQty(
    id: number,
    itemId: number,
    dto: UpdateQuoteItemDto,
  ): Observable<QuoteApi> {
    return this.http.patch<QuoteApi>(
      `${this.baseUrl}/${this.endpoint}/${id}/items/${itemId}`,
      dto,
    );
  }

  // DELETE /quotes/:id/items/:itemId (solo DRAFT)
  deleteItem(id: number, itemId: number): Observable<QuoteApi> {
    return this.http.delete<QuoteApi>(
      `${this.baseUrl}/${this.endpoint}/${id}/items/${itemId}`,
    );
  }

  // PATCH /quotes/:id/checkout  (DRAFT -> SUBMITTED)
  checkout(id: number): Observable<QuoteApi> {
    return this.http.patch<QuoteApi>(
      `${this.baseUrl}/${this.endpoint}/${id}/checkout`,
      {},
    );
  }

  // PATCH /quotes/:id/price (SUBMITTED -> QUOTED)  (staff/admin)
  price(id: number, dto: QuotePriceInputDto): Observable<QuoteApi> {
    return this.http.patch<QuoteApi>(
      `${this.baseUrl}/${this.endpoint}/${id}/price`,
      dto,
    );
  }

  // PATCH /quotes/:id/status (QUOTED -> APPROVED/REJECTED)  (cliente)
  setStatus(id: number, dto: QuoteStatusDto): Observable<QuoteApi> {
    return this.http.patch<QuoteApi>(
      `${this.baseUrl}/${this.endpoint}/${id}/status`,
      dto,
    );
  }

  // PATCH /quotes/:id/staff-approve (QUOTED -> APPROVED)  (staff, venta sin cliente)
  staffApprove(id: number): Observable<QuoteApi> {
    return this.http.patch<QuoteApi>(
      `${this.baseUrl}/${this.endpoint}/${id}/staff-approve`,
      {},
    );
  }

  // Helper: devuelve el DRAFT si existe
  findDraft(items: QuoteApi[]): QuoteApi | null {
    return items.find((q) => q.status === "DRAFT") ?? null;
  }

  ensureDraftAndAddItem(productId: number, qty: number): Observable<QuoteApi> {
    const safeQty = Math.max(1, Math.floor(Number(qty) || 1));

    // 1) buscar draft
    return this.list(1, 50).pipe(
      map((res) => this.findDraft(res.items ?? [])),
      switchMap((draft) => {
        // 2) si existe draft -> agregar item
        if (draft) {
          return this.addItem(draft.id, { productId, qty: safeQty });
        }

        // 3) si NO existe -> crear quote con items (TU BACKEND LO EXIGE)
        return this.create({
          items: [{ productId, qty: safeQty }],
        } as any);
      }),
    );
  }
}
